<template>
  <div>
    <h2><v-icon>mdi-chart-scatter-plot</v-icon> Visualisation</h2>
    <v-row>
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-card>
          <v-card-title>
            <template v-if="highlighted === null">
              {{ patients }}
            </template>
            <template v-else>
              <span class="highlighted">{{ highlighted }}</span
              >/{{ patients }}
            </template>
            <v-icon class="ml-2">mdi-account-supervisor</v-icon></v-card-title
          >
          <v-card-text>
            Number of patients
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-card>
          <v-card-title
            >{{ measurements
            }}<v-icon class="ml-2">mdi-file-chart</v-icon></v-card-title
          >
          <v-card-text>
            Number of measurements
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="$vuetify.breakpoint.mdAndUp">
      <v-col>
        <v-subheader>Positioning</v-subheader>
        <v-item-group v-model="graphPositioning" mandatory>
          <v-row>
            <v-col cols="6" md="4" lg="3">
              <v-item v-slot:default="{ active, toggle }">
                <v-card
                  @click="toggle"
                  :class="{ 'pos-card-selected': active }"
                  :style="{ height: '100%' }"
                >
                  <v-card-text class="positioning-card-text">
                    <div class="mb-2">
                      <v-icon
                        class="pr-2"
                        :style="{ borderRight: '2px solid gray' }"
                        >mdi-chart-scatter-plot</v-icon
                      ><v-icon class="ml-3">mdi-chart-scatter-plot</v-icon>
                    </div>
                    <p class="mb-0 ml-3">Side by side</p>
                  </v-card-text>
                </v-card>
              </v-item>
            </v-col>
            <v-col cols="6" md="4" lg="3">
              <v-item v-slot:default="{ active, toggle }">
                <v-card
                  @click="toggle"
                  :class="{ 'pos-card-selected': active }"
                >
                  <v-card-text class="positioning-card-text">
                    <div>
                      <v-icon
                        class="pb-2"
                        :style="{
                          borderBottom: '2px solid gray'
                        }"
                        >mdi-chart-scatter-plot</v-icon
                      >
                      <v-icon :style="{ display: 'block' }" class="mt-2"
                        >mdi-chart-scatter-plot</v-icon
                      >
                    </div>
                    <p class="mb-0 ml-3">Full width</p>
                  </v-card-text>
                </v-card>
              </v-item>
            </v-col>
          </v-row>
        </v-item-group>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-subheader>Colour</v-subheader>
        <v-checkbox
          class="mt-0"
          v-model="colorScores"
          label="Measurements"
        ></v-checkbox>
        <v-checkbox
          class="mt-0"
          v-model="colorModel"
          label="Model prediction"
        ></v-checkbox>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-subheader>X-axis</v-subheader>
        <v-row no-gutters>
          <v-col cols="12" sm="4" md="3" lg="2">
            <v-switch
              v-model="axisMaxAuto"
              :label="`Max value: ${axisMaxAuto ? 'auto' : 'manual'}`"
            ></v-switch>
          </v-col>
          <v-col cols="12" sm="4" md="3" lg="2">
            <v-text-field
              v-model="axisMax"
              :disabled="axisMaxAuto"
              type="number"
              min="0"
              label="Enter value"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-subheader>Patients</v-subheader>
        <v-checkbox
          class="mt-0"
          v-model="hideNoModel"
          label="Hide patients with no MLM data"
        ></v-checkbox>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-subheader>Download visualisations</v-subheader>
        <v-checkbox
          class="mt-0"
          v-model="addBackground"
          label="Add white background"
        >
        </v-checkbox>
        <v-btn
          color="primary"
          outlined
          class="download-btn mb-3"
          @click="downloadImage('MainChart')"
          ><v-icon class="mr-2">mdi-download</v-icon>Main chart</v-btn
        >
        <v-btn
          color="primary"
          outlined
          class="download-btn"
          @click="downloadImage('MeanChart')"
          ><v-icon class="mr-2">mdi-download</v-icon>Mean chart</v-btn
        >
      </v-col>
    </v-row>
  </div>
</template>

<style scoped>
.v-subheader {
  font-size: 1rem;
  padding-left: 0;
}
.axis-max-input {
  width: 50px;
}
.highlighted {
  color: #318779;
}
.download-btn {
  display: block;
}
.positioning-card-text {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.positioning-card-text i {
  font-size: 28px;
}

.pos-card-selected {
  border: 2px solid var(--v-primary-base);
}
</style>

<script>
import firebase from "@/firebase";
const functions = firebase.functions("europe-west2");
const addWhiteBackground = functions.httpsCallable("charts-addWhiteBackground");

export default {
  props: {
    patients: {
      type: String,
      default: "-"
    },
    measurements: {
      type: String,
      default: "-"
    },
    highlighted: {
      type: String,
      default: null
    }
  },
  data: () => ({
    graphPositioning: 0,
    addBackground: false
  }),
  computed: {
    colorScores: {
      get: function() {
        return this.$store.state.colorScores;
      },
      set: function(value) {
        this.$store.state.colorScores = value;
      }
    },
    colorModel: {
      get: function() {
        return this.$store.state.colorModel;
      },
      set: function(value) {
        this.$store.state.colorModel = value;
      }
    },
    hideNoModel: {
      get: function() {
        return this.$store.state.hideNoModel;
      },
      set: function(value) {
        this.$store.state.hideNoModel = value;
      }
    },
    axisMaxAuto: {
      get: function() {
        return this.$store.state.axisMaxAuto;
      },
      set: function(value) {
        this.$store.state.axisMaxAuto = value;
      }
    },
    axisMax: {
      get: function() {
        return this.$store.state.axisMax;
      },
      set: function(value) {
        this.$store.state.axisMax = parseInt(value);
      }
    }
  },
  methods: {
    async downloadImage(chartId) {
      // Get the chart canvas element
      let canvas = Array.from(document.getElementById(chartId).children).filter(
        x => x.id === "line-chart"
      )[0];

      // Get png image as DataURL
      let canvasData = canvas.toDataURL("image/png");

      // Create a new a element and set its href to the
      // dataURL encoded image
      let a = document.createElement("a");

      // Add white background if selected
      if (this.addBackground) {
        let img = await addWhiteBackground({ canvasData });
        a.href = "data:image/png;base64," + img.data;
      } else {
        a.href = canvasData;
      }

      // Set the photo name
      if (chartId === "MainChart") {
        a.download = "NPMC_scores_graph.png";
      } else if (chartId === "MeanChart") {
        a.download = "NPMC_mean_model_graph.png";
      }

      // Emulate the element click
      a.click();
    }
  },
  watch: {
    graphPositioning(value) {
      this.$emit("graphPositioningChanged", value);
    }
  }
};
</script>
